import { InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser'
import { msalInstance } from '@/auth/msalConfig'
import { parseClaudeSseStream } from './claudeSseParser'
import type { StreamEvent } from './streamReducer'
import type { DispatchedToolResult } from './toolDispatcher'
import type { ChatTransport, TurnContext } from './transport'
import type { ChatMessage } from './types'
import { buildUserContextLine } from './userContext'
import agentDefinition from '../../../../docs/foundry-agent/agent-definition.json'
import foundryTools from '../../../../docs/foundry-agent/tools.json'

/**
 * `ChatTransport` for Claude on Microsoft Foundry (the `/anthropic`
 * Messages-API surface, browser-direct with the user's delegated Entra
 * token — same auth model as `foundryTransport`, no API key anywhere).
 *
 * **LIVE.** `ChatProvider` selects this transport when
 * `VITE_CHAT_BACKEND=claude`, which the deploy workflow sets for both build
 * jobs. The CORS / RBAC / token-scope questions this started life as a spike
 * for are settled by the fact that it serves traffic: preflight succeeds from
 * the SWA origin, chat users hold **Cognitive Services User** on the Foundry
 * resource, and `CLAUDE_SCOPES` below (`https://ai.azure.com/.default`) is
 * the audience the endpoint accepts. Rollback is still one line —
 * `VITE_CHAT_BACKEND=foundry` and push.
 *
 * Where Foundry's Agent Service holds the model, instructions, and tools
 * server-side (agent-definition.json is *deployed* to the agent), the
 * Messages API is fully client-specified — so this transport carries the
 * same instructions + tools per request, imported from the same canonical
 * files the agent deploy script uses. One source of truth, two consumers.
 * (Vite note: these JSON imports resolve outside web/'s root — fine at
 * build time; `vite dev` may need `server.fs.allow` widened when wiring.)
 *
 * Statelessness is the other structural difference: there is no
 * `previous_response_id`. Every request replays `ctx.history` mapped to
 * Anthropic messages, reconstructing `tool_use`/`tool_result` pairs from
 * the reducer's ToolCall rows (each dispatch round lives in its own
 * assistant bubble, so alternation reconstructs faithfully). `threadId`
 * is ignored.
 */

const CLAUDE_SCOPES = ['https://ai.azure.com/.default']

const MESSAGES_PATH = '/anthropic/v1/messages'

/**
 * Required by the Messages API on every request. This is a hard ceiling on
 * the whole response, so a low value truncates long answers mid-sentence.
 * We always stream, so the HTTP-timeout reason to keep it small doesn't
 * apply — 64k is the normal streaming default for this model tier.
 */
const MAX_TOKENS = 64000
const ANTHROPIC_VERSION = '2023-06-01'

/**
 * Prompt-cache breakpoint marker. 1h rather than the 5m default because chat
 * turns are separated by however long the user takes to read the previous
 * answer, and a 5m window expires across ordinary think time — the extra
 * write cost is repaid by the first read that would otherwise have been a
 * full-price re-send of the whole prefix.
 */
const CACHE_CONTROL = { type: 'ephemeral', ttl: '1h' } as const

/**
 * Time-to-first-byte budget. Applies to the fetch only — cleared the moment
 * response headers arrive, so a long legitimate stream is never cut off
 * mid-answer. A mid-stream *idle* timeout would need the reader instrumented
 * inside the parser; that's a separate change.
 */
const REQUEST_TIMEOUT_MS = 120_000

/** Total attempts per request, including the first. Only 429s are retried. */
const MAX_ATTEMPTS = 3

/** Ceiling on any single backoff wait, so a wedged quota can't park the UI. */
const MAX_RETRY_DELAY_MS = 20_000

const resourceEndpoint = import.meta.env.VITE_CLAUDE_RESOURCE_ENDPOINT
const deployment = import.meta.env.VITE_CLAUDE_DEPLOYMENT

// ---------------------------------------------------------------------------
// Agent definition — instructions + tools from the canonical files
// ---------------------------------------------------------------------------

interface AnthropicTool {
  name: string
  description: string
  input_schema: Record<string, unknown>
}

/**
 * tools.json is emitted in Foundry function-tool shape
 * (`{type, name, description, parameters}`) by `export-foundry-schemas`;
 * Anthropic wants `{name, description, input_schema}`. Same JSON Schema
 * inside — only the envelope key differs.
 */
const ANTHROPIC_TOOLS: AnthropicTool[] = (
  foundryTools as Array<{ name: string; description: string; parameters: unknown }>
).map(tool => ({
  name: tool.name,
  description: tool.description,
  input_schema: tool.parameters as Record<string, unknown>,
}))

const AGENT_INSTRUCTIONS: string = agentDefinition.definition.instructions

// ---------------------------------------------------------------------------
// Messages API request shapes
// ---------------------------------------------------------------------------

type CacheControl = typeof CACHE_CONTROL

type TextBlock = { type: 'text'; text: string; cache_control?: CacheControl }
type ToolUseBlock = {
  type: 'tool_use'
  id: string
  name: string
  input: Record<string, unknown>
  cache_control?: CacheControl
}
type ToolResultBlock = {
  type: 'tool_result'
  tool_use_id: string
  content: string
  is_error?: boolean
  cache_control?: CacheControl
}

interface AnthropicMessage {
  role: 'user' | 'assistant'
  content: Array<TextBlock | ToolUseBlock | ToolResultBlock>
}

interface MessagesRequest {
  model: string
  max_tokens: number
  /** Block form (not a bare string) so a `cache_control` breakpoint fits. */
  system: TextBlock[]
  tools: AnthropicTool[]
  messages: AnthropicMessage[]
  stream: true
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

export const claudeTransport: ChatTransport = {
  configMissing: !resourceEndpoint || !deployment,

  async *startTurn(message: string, ctx: TurnContext) {
    yield* withClaudeToken(ctx, () => {
      const messages = mergeConsecutive([
        ...toAnthropicMessages(ctx.history),
        { role: 'user', content: [{ type: 'text', text: message }] },
      ])
      return buildRequest(messages, ctx.account)
    })
  },

  async *submitToolOutputs(results: DispatchedToolResult[], ctx: TurnContext) {
    yield* withClaudeToken(ctx, () => {
      // The history snapshot's last assistant bubble holds the tool_use
      // rows being answered (their resultJson is still null there — the
      // hook snapshots history before results land in the reducer), so the
      // matching tool_result blocks come from `results`. `answered` guards
      // against double-emitting if that snapshot timing ever changes.
      const answered = new Set(results.map(r => r.callId))
      const messages = mergeConsecutive([
        ...toAnthropicMessages(ctx.history, answered),
        {
          role: 'user',
          content: results.map((r): ToolResultBlock => {
            const block: ToolResultBlock = {
              type: 'tool_result',
              tool_use_id: r.callId,
              content: r.output,
            }
            if (!r.ok) block.is_error = true
            return block
          }),
        },
      ])
      return buildRequest(messages, ctx.account)
    })
  },

  // respondToApproval / resumeAfterConsent intentionally omitted: the
  // approval and OAuth-consent gates are Foundry Custom-MCP artefacts.
  // This path (like the Foundry function-tool path) never emits them.
}

// ---------------------------------------------------------------------------
// Request building
// ---------------------------------------------------------------------------

function buildRequest(messages: AnthropicMessage[], account: AccountInfo): MessagesRequest {
  // Stateless backend → the identity context rides in `system` on every
  // request instead of Foundry's first-turn developer message. It goes in its
  // own block AFTER the cache breakpoint: caching is a prefix match, so a
  // per-user line folded into the instructions string (as it used to be)
  // would give every user a different prefix and cache nothing.
  const contextLine = buildUserContextLine(account)
  const instructions: TextBlock = {
    type: 'text',
    text: AGENT_INSTRUCTIONS,
    // Render order is tools → system → messages, so this one breakpoint
    // caches the tool array *and* the instructions together — ~8.5k tokens
    // that were otherwise re-billed as uncached input on every request,
    // including every tool-dispatch round within a single turn. That is what
    // exhausted the per-user `…ByMinuteUncachedInputTokens` quota.
    cache_control: CACHE_CONTROL,
  }
  return {
    model: deployment as string,
    max_tokens: MAX_TOKENS,
    system: contextLine ? [instructions, { type: 'text', text: contextLine }] : [instructions],
    tools: ANTHROPIC_TOOLS,
    messages: withHistoryBreakpoint(messages),
    stream: true,
  }
}

/**
 * Adds a second cache breakpoint at the end of the conversation so the next
 * request in the same turn — a tool-dispatch round, which replays everything
 * above plus one `tool_result` — reads the history prefix instead of
 * re-billing it. Two breakpoints total, well inside the 4-per-request limit.
 *
 * Returns copies: mutating the caller's blocks would leak `cache_control`
 * into the reducer-owned history that later requests rebuild from.
 */
function withHistoryBreakpoint(messages: AnthropicMessage[]): AnthropicMessage[] {
  const lastIdx = messages.length - 1
  const last = messages[lastIdx]
  if (!last || last.content.length === 0) return messages

  const content = last.content.slice()
  const tailIdx = content.length - 1
  const tail = content[tailIdx]
  if (!tail) return messages
  content[tailIdx] = { ...tail, cache_control: CACHE_CONTROL }

  const out = messages.slice()
  out[lastIdx] = { ...last, content }
  return out
}

/**
 * Maps the reducer's ChatMessages to Anthropic messages, reconstructing
 * tool exchanges: an assistant bubble's ToolCall rows become `tool_use`
 * blocks, and their stored results become a following user message of
 * `tool_result` blocks. Rows without results yet (or whose ids are in
 * `excludeResultIds`) emit no result block — the caller appends those.
 */
function toAnthropicMessages(
  history: ChatMessage[],
  excludeResultIds?: Set<string>,
): AnthropicMessage[] {
  const out: AnthropicMessage[] = []
  for (const m of history) {
    if (m.role === 'user') {
      if (m.text.trim() !== '') {
        out.push({ role: 'user', content: [{ type: 'text', text: m.text }] })
      }
      continue
    }

    const content: Array<TextBlock | ToolUseBlock> = []
    if (m.text.trim() !== '') {
      content.push({ type: 'text', text: m.text })
    }
    for (const tc of m.toolCalls) {
      if (!tc.id || !tc.name) continue
      content.push({ type: 'tool_use', id: tc.id, name: tc.name, input: parseArgs(tc.argsJson) })
    }
    if (content.length > 0) {
      out.push({ role: 'assistant', content })
    }

    const resultBlocks: ToolResultBlock[] = []
    for (const tc of m.toolCalls) {
      if (tc.resultJson === null) continue
      if (excludeResultIds?.has(tc.id)) continue
      const block: ToolResultBlock = {
        type: 'tool_result',
        tool_use_id: tc.id,
        content: tc.resultJson,
      }
      if (tc.state === 'failed') block.is_error = true
      resultBlocks.push(block)
    }
    if (resultBlocks.length > 0) {
      out.push({ role: 'user', content: resultBlocks })
    }
  }
  return out
}

function parseArgs(argsJson: string): Record<string, unknown> {
  if (argsJson.trim() === '') return {}
  try {
    const parsed = JSON.parse(argsJson) as unknown
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    // fall through — the model sees an empty-args call rather than a crash
  }
  return {}
}

/**
 * Merges adjacent same-role messages (defensive: the API wants alternating
 * turns). Block order is preserved, which keeps `tool_result` blocks at the
 * front of a merged user message — where the API requires them.
 */
function mergeConsecutive(messages: AnthropicMessage[]): AnthropicMessage[] {
  const out: AnthropicMessage[] = []
  for (const m of messages) {
    const prev = out[out.length - 1]
    if (prev && prev.role === m.role) {
      prev.content = [...prev.content, ...m.content]
    } else {
      out.push({ role: m.role, content: [...m.content] })
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Networking — mirrors foundryTransport's error contract: failures become a
// single stream.error event; aborts end the stream silently; never throws.
// Adds two things that contract doesn't cover: 429s are retried with backoff
// before surfacing, and each attempt has a time-to-first-byte budget.
// ---------------------------------------------------------------------------

async function* withClaudeToken(
  ctx: TurnContext,
  makeBody: () => MessagesRequest,
): AsyncGenerator<StreamEvent> {
  let token: string
  try {
    token = await acquireClaudeToken(ctx.account)
  } catch (err) {
    yield {
      kind: 'stream.error',
      message: err instanceof Error ? err.message : 'Token acquisition failed',
    }
    return
  }
  yield* streamMessages(makeBody(), token, ctx.signal)
}

async function* streamMessages(
  body: MessagesRequest,
  token: string,
  signal: AbortSignal,
): AsyncGenerator<StreamEvent> {
  const payload = JSON.stringify(body)

  for (let attempt = 1; ; attempt++) {
    // Retries are safe only before any bytes reach the reducer, which holds
    // because 429 is decided from response headers — once we start yielding
    // parsed events we never re-issue the request.
    const budget = timeToFirstByteBudget(signal, REQUEST_TIMEOUT_MS)
    let response: Response
    try {
      response = await fetch(`${resourceEndpoint}${MESSAGES_PATH}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          'anthropic-version': ANTHROPIC_VERSION,
        },
        body: payload,
        signal: budget.signal,
      })
    } catch (err) {
      if (signal.aborted) return // user cancelled — silent, per the contract
      if (budget.timedOut) {
        yield {
          kind: 'stream.error',
          message: `Claude did not respond within ${REQUEST_TIMEOUT_MS / 1000}s. Please try again.`,
        }
        return
      }
      yield {
        kind: 'stream.error',
        message: err instanceof Error ? err.message : 'Stream failed',
      }
      return
    } finally {
      // Headers are in (or the attempt failed): stop the clock so a long
      // legitimate stream isn't aborted mid-answer.
      budget.clear()
    }

    if (response.status === 429 && attempt < MAX_ATTEMPTS) {
      const delay = retryDelayMs(response, attempt)
      // Release the quota-error body rather than leaving it dangling for the
      // GC while we wait — we never read it on this path.
      void response.body?.cancel().catch(() => {})
      if (!(await sleep(delay, signal))) return
      continue
    }

    if (!response.ok) {
      const errBody = await response.text().catch(() => '')
      yield { kind: 'stream.error', message: describeHttpError(response, errBody) }
      return
    }

    if (!response.body) {
      yield { kind: 'stream.error', message: 'Response body is empty' }
      return
    }

    try {
      for await (const event of parseClaudeSseStream(response.body)) {
        if (signal.aborted) break
        yield event
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      yield {
        kind: 'stream.error',
        message: err instanceof Error ? err.message : 'Stream failed',
      }
    }
    return
  }
}

/**
 * Turns a non-OK response into user-facing text. 429 gets a plain-language
 * message because the raw Foundry body is an unreadable quota blob (and the
 * 200-char slice below cut it mid-sentence, which is how this surfaced as a
 * bug report). Everything else keeps the sliced body — unhelpful to users but
 * the fastest signal we have while this transport is young.
 */
function describeHttpError(response: Response, body: string): string {
  if (response.status === 429) {
    return 'Orion is handling too many requests right now. Wait a few seconds and send your message again.'
  }
  return `Claude ${response.status}: ${body.slice(0, 200) || response.statusText}`
}

/**
 * Seconds to wait before retrying a 429. Prefers Foundry's `retry-after`
 * (its 429 body asks for a specific wait), falling back to exponential
 * backoff — including when the header is an HTTP-date rather than a count of
 * seconds, which `Number()` reports as NaN. Jittered so several open tabs
 * don't retry in lockstep.
 */
function retryDelayMs(response: Response, attempt: number): number {
  const header = response.headers.get('retry-after')
  const seconds = header === null ? Number.NaN : Number(header)
  const advised = Number.isFinite(seconds) ? seconds * 1000 : Number.NaN
  const backoff = 1000 * 2 ** (attempt - 1)
  const base = Number.isFinite(advised) ? advised : backoff
  return Math.min(base, MAX_RETRY_DELAY_MS) + Math.random() * 250
}

/**
 * Resolves `false` if the wait was cut short by the caller aborting. Whichever
 * of the two paths runs second is harmless: `clearTimeout` on a fired timer is
 * a no-op, and a second `resolve` is ignored.
 */
function sleep(ms: number, signal: AbortSignal): Promise<boolean> {
  if (signal.aborted) return Promise.resolve(false)
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(true), ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        resolve(false)
      },
      { once: true },
    )
  })
}

/**
 * A child signal that aborts on either the caller's signal (user cancel) or a
 * timeout, with `timedOut` to tell them apart — `AbortError` alone cannot,
 * and the two need opposite handling: a cancel stays silent, a timeout must
 * surface an error.
 *
 * `clear()` stops the timer but deliberately leaves the caller's abort wired
 * through, so cancelling mid-stream still tears down the body read.
 */
function timeToFirstByteBudget(
  outer: AbortSignal,
  ms: number,
): { signal: AbortSignal; readonly timedOut: boolean; clear: () => void } {
  const controller = new AbortController()
  let timedOut = false

  if (outer.aborted) controller.abort()
  else outer.addEventListener('abort', () => controller.abort(), { once: true })

  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, ms)

  return {
    signal: controller.signal,
    get timedOut() {
      return timedOut
    },
    clear: () => clearTimeout(timer),
  }
}

async function acquireClaudeToken(account: AccountInfo): Promise<string> {
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: CLAUDE_SCOPES,
      account,
    })
    return result.accessToken
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // Same popup-over-redirect rationale as foundryTransport: keep the
      // SPA tab (and the typed message + thread) alive through first-time
      // consent.
      const result = await msalInstance.acquireTokenPopup({
        scopes: CLAUDE_SCOPES,
        account,
      })
      return result.accessToken
    }
    throw err
  }
}
